import {
  BarChart2,
  Filter,
  type LucideProps,
  Monitor,
  Sparkles,
  TrendingUp,
  Users,
} from "lucide-react";
import type { ComponentType } from "react";
import type { FeatureItem } from "../data/loginFeatures";
import { cn } from "@/lib/utils";
import { VectaIcon } from "@/components/logos/VectaIcon";

export const featureIconMap: Record<string, ComponentType<LucideProps>> = {
  sparkles: Sparkles,
  vecta: Sparkles,
  users: Users,
  filter: Filter,
  barChart2: BarChart2,
  monitor: Monitor,
  trendingUp: TrendingUp,
};

type Props = {
  items: FeatureItem[];
};

/**
 * Renders a responsive grid of feature tiles.
 * Handles any number of items gracefully:
 *   - Single column on small viewports
 *   - Two columns on 2xl+ viewports
 *   - Odd-count last item spans full width to avoid orphan half-rows
 */
export const FeatureTiles = ({ items }: Props) => {
  if (items.length === 0) return null;

  return (
    <div className="grid grid-cols-1 2xl:grid-cols-2 gap-[clamp(0.5rem,0.8vw,1.25rem)]">
      {items.map(({ iconName, title, desc }, i) => {
        const Icon = featureIconMap[iconName] ?? Monitor;
        // Span full width at 2xl if this is the only remaining item in an odd row
        const isOrphan = items.length % 2 !== 0 && i === items.length - 1;
        return (
          <div
            key={i}
            className={cn("bg-[#252B42] rounded-xl p-[clamp(0.875rem,1.2vw,1.5rem)]", isOrphan && "2xl:col-span-2")}
          >
            <div className={cn(iconName === "vecta" && "-ml-1")}>
              {iconName === "vecta"
                ? <span><VectaIcon size={22} /></span>
                : <Icon size={20} className="text-brand-pink" strokeWidth={2} />}
            </div>
            <p className="text-white font-semibold text-[clamp(0.8rem,0.9vw,1.05rem)] mt-[0.4rem] mb-1">
              {title}
            </p>
            <p className="text-[#9CA3AF] text-[clamp(0.7rem,0.82vw,0.95rem)] leading-normal m-0">
              {desc}
            </p>
          </div>
        );
      })}
    </div>
  );
};
